// Pure rules for the plan, entitlement and usage screens (spec 15 §4 S6, S7,
// S12; M15-6, M15-7a). The server enforces every one of them; the console
// mirrors them only to decide what to offer and how to say it.

import type { TFunction } from "i18next";
import { PILOT_PLAN_CODE } from "@assessly/contracts";
import { PlatformError, type PlatformRole, type PlatformScope } from "./platform-api";
import type { PlanView, TenantUsage, UsageMetric } from "./plans-api";
import { enumLabel } from "../../../lib/enum-label";

/** Plans are edited by platform_admin only (spec 15 §7). */
export function canEditPlans(role: PlatformRole): boolean {
  return role === "platform_admin";
}

/** Overrides, plan moves, rename and subdomain: platform_admin (spec 15 §7). */
export function canEditTenant(role: PlatformRole): boolean {
  return role === "platform_admin";
}

/** A sandbox member edits sandbox plans only (D-615 ruling 3). */
export function canEditPlan(role: PlatformRole, scope: PlatformScope, plan: PlanView): boolean {
  return canEditPlans(role) && plan.status !== "retired" && (scope === "fleet" || plan.sandbox);
}

/** Pilot is never retired: every tenant without a plan is on it (409
 * PILOT_PLAN_REQUIRED). */
export function canRetirePlan(role: PlatformRole, scope: PlatformScope, plan: PlanView): boolean {
  return canEditPlan(role, scope, plan) && plan.code !== PILOT_PLAN_CODE;
}

/**
 * The plans a tenant may be put on: not retired; a sandbox plan only for a
 * sandbox tenant (409 PLAN_SANDBOX_ONLY otherwise); and, when the pack is
 * known, one that switches the pack on (422 PACK_NOT_IN_PLAN otherwise).
 */
export function assignablePlans(
  plans: readonly PlanView[],
  input: { sandboxTenant: boolean; pack: string | null },
): PlanView[] {
  return plans.filter(
    (p) =>
      p.status !== "retired" &&
      (input.sandboxTenant || !p.sandbox) &&
      (input.pack === null ||
        p.entitlements.some((e) => e.module_code === `vertical_pack:${input.pack}` && e.enabled)),
  );
}

/** A module code's label: a pack by its own name, anything else from the catalogue. */
export function moduleLabel(code: string, t: TFunction, packName: (key: string) => string): string {
  if (code.startsWith("vertical_pack:")) {
    return t("admin.entitlements.packCode", {
      pack: packName(code.slice("vertical_pack:".length)),
    });
  }
  return enumLabel(t, "admin.moduleCodes", code);
}

/** An amount in minor units, in the currency's own decimals. */
export function formatMinor(minor: number, currency: string, language: string): string {
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // an unknown code keeps two decimals
  }
  const value = minor / 10 ** digits;
  try {
    return new Intl.NumberFormat(language, { style: "currency", currency }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

/** Major units typed by the operator, to minor units; null when not a number. */
export function toMinor(major: string, currency: string): number | null {
  const cleaned = major.replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(?:\.\d+)?$/.test(cleaned)) return null;
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // two decimals
  }
  const [whole = "0", frac = ""] = cleaned.split(".");
  if (frac.length > digits) return null;
  const minor = Number(whole) * 10 ** digits + Number(frac.padEnd(digits, "0") || "0");
  return Number.isSafeInteger(minor) ? minor : null;
}

export function majorFromMinor(minor: number, currency: string): string {
  let digits = 2;
  try {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    // two decimals
  }
  return digits === 0 ? String(minor) : (minor / 10 ** digits).toFixed(digits);
}

/** Bytes in the largest binary unit that keeps the figure readable. */
export function formatBytes(bytes: number, language: string): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const n = new Intl.NumberFormat(language, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(
    value,
  );
  return `${n} ${units[unit] ?? "B"}`;
}

export function formatCount(n: number, language: string): string {
  return new Intl.NumberFormat(language).format(n);
}

/** `YYYY-MM` of a date, in UTC like the usage tables. */
export function utcMonth(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/** The months the picker offers: the current one and the twelve before it
 * (the 400-day retention keeps about thirteen). */
export function pickerMonths(now: Date): string[] {
  const out: string[] = [];
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  for (let i = 0; i < 13; i += 1) {
    const d = new Date(Date.UTC(y, m - i, 1));
    out.push(utcMonth(d));
  }
  return out;
}

export function monthLabel(month: string, language: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** One CSV field, quoted when it must be (RFC 4180). */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * The usage tab's export: one row per day, one column per metric, the day
 * first, values as the API served them and an empty cell for a day with no
 * reading (never a 0 the meter did not record).
 */
export function usageCsv(
  usage: TenantUsage,
  header: { date: string; metric: (m: UsageMetric) => string },
): string {
  const metrics = usage.series.map((s) => s.metric);
  const days = usage.series[0]?.points.map((p) => p.date) ?? [];
  const lines = [[header.date, ...metrics.map(header.metric)].map(csvField).join(",")];
  for (let i = 0; i < days.length; i += 1) {
    const cells = usage.series.map((s) => {
      const v = s.points[i]?.value;
      return v === null || v === undefined ? "" : String(v);
    });
    lines.push([days[i] ?? "", ...cells].map(csvField).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}

/** What a refused plan or entitlement write means for the operator. */
export type PlanFailure =
  | "scope"
  | "role"
  | "code_taken"
  | "retired"
  | "pilot"
  | "sandbox_only"
  | "pack"
  | "below_usage"
  | "thresholds"
  | "unknown_module"
  | "invalid"
  | "other";

export function planFailure(error: unknown): PlanFailure {
  if (!(error instanceof PlatformError)) return "other";
  switch (error.code) {
    case "STAFF_SCOPE_DENIED":
      return "scope";
    case "PERMISSION_DENIED":
      return "role";
    case "PLAN_CODE_TAKEN":
      return "code_taken";
    case "PLAN_RETIRED":
      return "retired";
    case "PILOT_PLAN_REQUIRED":
      return "pilot";
    case "PLAN_SANDBOX_ONLY":
      return "sandbox_only";
    case "PACK_NOT_IN_PLAN":
      return "pack";
    case "LIMIT_BELOW_USAGE":
      return "below_usage";
    case "LIMIT_THRESHOLDS_INVALID":
      return "thresholds";
    case "MODULE_CODE_UNKNOWN":
      return "unknown_module";
    case "OVERRIDE_INVALID":
    case "VALIDATION_FAILED":
      return "invalid";
    default:
      return error.status === 403 ? "role" : "other";
  }
}
